import type { Backtest } from "../types/backtests";
import type { Trade } from "../types/trade";
import type { TradePolicySimulateResponse } from "../types/tradePolicy";
import { isTradeManagementBacktest } from "./backtestSimulationOverlay";
import {
  normalizeTradeOverlayId,
  unwrapSimulatedTradeRow,
  type SimulatedTradeRow,
} from "./simulationTradeOverlay";

export type TradingPnlSeriesPoint = { timeMs: number; cumulativePnl: number };

export type TradingPnlSeries = {
  id: string;
  label: string;
  color: string;
  points: TradingPnlSeriesPoint[];
};

/**
 * 16 categorical colors for report sim overlays (matches MAX_REPORT_PNL_LAYERED_SIMS).
 * Hue-spaced for dark charts — one per family so all 16 stay distinguishable together.
 */
export const REPORT_SIM_PNL_LINE_COLORS = [
  "#F49113", // orange
  "#06E363", // green
  "#C084FC", // purple
  "#FE4244", // red
  "#3B82F6", // blue
  "#FBBF24", // amber
  "#F472B6", // pink
  "#14B8A6", // teal
  "#A3E635", // lime
  "#818CF8", // indigo
  "#FB7185", // rose
  "#E879F9", // fuchsia
  "#84CC16", // chartreuse
  "#F59E0B", // gold
  "#6366F1", // violet-blue
  "#EC4899", // magenta
] as const;

export const REPORT_BASELINE_PNL_LINE_COLOR = "#9CA3AF";

export function colorForReportSimIndex(index: number): string {
  return REPORT_SIM_PNL_LINE_COLORS[index % REPORT_SIM_PNL_LINE_COLORS.length] ?? "#3B82F6";
}

/**
 * Sticky sim line colors keyed by backtest id.
 * Preserves prior assignments so adding/removing/reordering sims does not reshuffle colors.
 */
export function assignStickyReportSimColors(
  simIds: string[],
  previous: Record<string, string> = {}
): Record<string, string> {
  const ids = simIds.map((id) => String(id ?? "").trim()).filter(Boolean);
  const next: Record<string, string> = {};
  const used = new Set<string>();

  for (const id of ids) {
    const prior = String(previous[id] ?? "").trim();
    if (!prior) continue;
    next[id] = prior;
    used.add(prior);
  }

  for (const id of ids) {
    if (next[id]) continue;
    const free = REPORT_SIM_PNL_LINE_COLORS.find((c) => !used.has(c));
    const color = free ?? colorForReportSimIndex(Object.keys(next).length);
    next[id] = color;
    used.add(color);
  }

  return next;
}

/** Max simulation lines that can be overlaid on the report trading PnL chart. */
export const MAX_REPORT_PNL_LAYERED_SIMS = 16;

/** Brush zoom window stored by exit time so sim toggles do not reset the range. */
export type ReportPnlBrushWindow = { startMs: number; endMs: number };

type PnlEvent = { timeMs: number; pnl: number };

function parseTimeMs(raw: unknown): number | null {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return raw > 1e12 ? raw : raw * 1000;
  }
  const ms = new Date(String(raw)).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** Exit timestamp used for PnL series / as-of clipping. */
export function tradeExitTimeMs(t: Trade): number | null {
  const row = t as Trade & Record<string, unknown>;
  return parseTimeMs(t.exitDate ?? row.exit_date ?? row.exit_time);
}

/** Keep trades whose exit is on or before `asOfEndMs` (inclusive). */
export function filterTradesExitedOnOrBefore(
  trades: Trade[],
  asOfEndMs: number | null | undefined
): Trade[] {
  if (asOfEndMs == null || !Number.isFinite(asOfEndMs)) return trades;
  return trades.filter((t) => {
    const ms = tradeExitTimeMs(t);
    return ms != null && ms <= asOfEndMs;
  });
}

/** Drop series points after the as-of instant (keeps end-of-day points on that day). */
export function clampTradingPnlSeriesToAsOf(
  series: TradingPnlSeries,
  asOfEndMs: number | null | undefined
): TradingPnlSeries {
  if (asOfEndMs == null || !Number.isFinite(asOfEndMs)) return series;
  return {
    ...series,
    points: series.points.filter((p) => p.timeMs <= asOfEndMs),
  };
}

function readRowField(row: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) {
    if (row[key] != null && row[key] !== "") return row[key];
  }
  return null;
}

function pnlFromPrices(
  direction: unknown,
  entry: unknown,
  exit: unknown
): number | null {
  if (entry == null || exit == null) return null;
  const entryN = Number(entry);
  const exitN = Number(exit);
  if (!Number.isFinite(entryN) || !Number.isFinite(exitN)) return null;
  const isShort =
    direction === "short" ||
    direction === "sell" ||
    direction === "SELL";
  return isShort ? entryN - exitN : exitN - entryN;
}

function pnlEventFromOverlayRow(row: Record<string, unknown>): PnlEvent | null {
  const exitRaw = readRowField(row, "exit_time", "exit_date", "exitDate");
  const timeMs = parseTimeMs(exitRaw);
  if (timeMs == null) return null;

  const netRaw = readRowField(row, "net_pnl", "netPnL", "sim_net_pnl");
  let pnl =
    netRaw != null && Number.isFinite(Number(netRaw)) ? Number(netRaw) : null;
  if (pnl == null) {
    pnl = pnlFromPrices(
      readRowField(row, "direction", "type", "trade_type"),
      readRowField(row, "entry_price", "entryPrice"),
      readRowField(row, "exit_price", "exitPrice")
    );
  }
  if (pnl == null || !Number.isFinite(pnl)) return null;
  return { timeMs, pnl };
}

function pnlEventFromTrade(t: Trade): PnlEvent | null {
  const row = t as Trade & Record<string, unknown>;
  const timeMs = tradeExitTimeMs(t);
  if (timeMs == null) return null;

  let pnl: number | null = null;
  if (t.netPnL != null && Number.isFinite(Number(t.netPnL))) pnl = Number(t.netPnL);
  if (pnl == null && row.net_pnl != null && Number.isFinite(Number(row.net_pnl))) {
    pnl = Number(row.net_pnl);
  }
  if (pnl == null && t.grossPnL != null && Number.isFinite(Number(t.grossPnL))) {
    pnl = Number(t.grossPnL);
  }
  if (pnl == null) {
    pnl = pnlFromPrices(t.type, t.entryPrice, t.exitPrice);
  }
  if (pnl == null || !Number.isFinite(pnl)) return null;
  return { timeMs, pnl };
}

function buildCumulativeFromEvents(events: PnlEvent[]): TradingPnlSeriesPoint[] {
  const sorted = [...events].sort((a, b) => a.timeMs - b.timeMs);
  let run = 0;
  return sorted.map((e) => {
    run += e.pnl;
    return { timeMs: e.timeMs, cumulativePnl: run };
  });
}

/** Local calendar day start for bucketing exits onto one plot per day. */
export function startOfLocalDayMs(timeMs: number): number {
  const d = new Date(timeMs);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * Collapse exit-level cumulative points to one point per calendar day.
 * Value = cumulative PnL after the last exit that day (end-of-day achieved).
 */
export function toDailyEndOfDayPoints(
  points: TradingPnlSeriesPoint[]
): TradingPnlSeriesPoint[] {
  const sorted = [...points].sort((a, b) => a.timeMs - b.timeMs);
  const byDay = new Map<number, number>();
  for (const p of sorted) {
    byDay.set(startOfLocalDayMs(p.timeMs), p.cumulativePnl);
  }
  return Array.from(byDay.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([timeMs, cumulativePnl]) => ({ timeMs, cumulativePnl }));
}

function formatTradingPnlDayLabel(timeMs: number): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  }).format(new Date(timeMs));
}

function collectEventsFromTrades(trades: Trade[]): PnlEvent[] {
  const events: PnlEvent[] = [];
  for (const t of trades) {
    const evt = pnlEventFromTrade(t);
    if (evt) events.push(evt);
  }
  return events;
}

function collectEventsFromOverlayRows(
  rows: Array<Record<string, unknown>> | undefined
): PnlEvent[] {
  const events: PnlEvent[] = [];
  for (const raw of rows ?? []) {
    const evt = pnlEventFromOverlayRow(raw);
    if (evt) events.push(evt);
  }
  return events;
}

function unionBaselineEvents(trades: Trade[], linkedBacktests: Backtest[]): PnlEvent[] {
  const events = collectEventsFromTrades(trades);
  if (events.length > 0) return events;

  for (const bt of linkedBacktests) {
    const result = bt.result as TradePolicySimulateResponse | undefined;
    const fromBaseline = collectEventsFromOverlayRows(result?.baseline_trades);
    if (fromBaseline.length > 0) return fromBaseline;
    const fromSim = collectEventsFromOverlayRows(result?.simulated_trades);
    if (fromSim.length > 0) return fromSim;
  }
  return events;
}

export function buildBaselineTradingPnlSeries(
  trades: Trade[],
  linkedBacktests: Backtest[] = [],
  label = "Baseline"
): TradingPnlSeries {
  return {
    id: "baseline",
    label,
    color: REPORT_BASELINE_PNL_LINE_COLOR,
    points: buildCumulativeFromEvents(unionBaselineEvents(trades, linkedBacktests)),
  };
}

function readNetPnlFromRecord(row: Record<string, unknown>): number | null {
  const netRaw = readRowField(row, "net_pnl", "netPnL", "sim_net_pnl");
  if (netRaw != null && Number.isFinite(Number(netRaw))) return Number(netRaw);
  return pnlFromPrices(
    readRowField(row, "direction", "type", "trade_type"),
    readRowField(row, "entry_price", "entryPrice"),
    readRowField(row, "exit_price", "exitPrice")
  );
}

function simRowNetPnl(row: SimulatedTradeRow, fallbackTrade?: Trade): number | null {
  const { simulated } = unwrapSimulatedTradeRow(row);
  const fromSim = readNetPnlFromRecord(simulated as Record<string, unknown>);
  if (fromSim != null) return fromSim;
  return fallbackTrade ? pnlEventFromTrade(fallbackTrade)?.pnl ?? null : null;
}

function simRowExitMs(row: SimulatedTradeRow, fallbackTrade?: Trade): number | null {
  const { simulated, baseline } = unwrapSimulatedTradeRow(row);
  return (
    parseTimeMs(simulated.exit_time) ??
    parseTimeMs((baseline as Record<string, unknown> | undefined)?.exit_time) ??
    (fallbackTrade ? tradeExitTimeMs(fallbackTrade) : null)
  );
}

export function buildBacktestTradingPnlSeries(
  backtest: Backtest,
  trades: Trade[],
  color: string,
  options?: { treatAsTradeManagement?: boolean }
): TradingPnlSeries | null {
  const result = backtest.result as TradePolicySimulateResponse | undefined;
  if (!result) return null;

  const label = backtest.title?.trim() || `Sim ${backtest.id.slice(0, 8)}…`;
  const tradeById = new Map(trades.map((t) => [normalizeTradeOverlayId(t.id), t]));
  const isTm =
    options?.treatAsTradeManagement === true || isTradeManagementBacktest(backtest);
  const events: PnlEvent[] = [];

  if (isTm) {
    // Exit-placement rows are nested `{ baseline, simulated, delta }`. Never read
    // PnL from the wrapper (or fall back to report trades) before checking
    // `simulated.net_pnl` — otherwise the chart matches baseline.
    for (const raw of result.simulated_trades ?? []) {
      const row = raw as unknown as SimulatedTradeRow;
      const { tradeId } = unwrapSimulatedTradeRow(row);
      const id = normalizeTradeOverlayId(tradeId);
      const baseTrade = id ? tradeById.get(id) : undefined;
      const ms = simRowExitMs(row, baseTrade);
      const pnl = simRowNetPnl(row, baseTrade);
      if (ms == null || pnl == null) continue;
      events.push({ timeMs: ms, pnl });
    }
  } else {
    const fromSimRows = collectEventsFromOverlayRows(result.simulated_trades);
    if (fromSimRows.length > 0) {
      events.push(...fromSimRows);
    } else {
      const removed = new Set(
        (result.removed_trade_ids ?? []).map((id) => normalizeTradeOverlayId(id))
      );
      for (const t of trades) {
        if (removed.has(normalizeTradeOverlayId(t.id))) continue;
        const evt = pnlEventFromTrade(t);
        if (evt) events.push(evt);
      }
      if (events.length === 0) {
        for (const raw of result.simulated_trades ?? result.baseline_trades ?? []) {
          const id = normalizeTradeOverlayId(
            String((raw as Record<string, unknown>).trade_id ?? "")
          );
          if (id && removed.has(id)) continue;
          const evt = pnlEventFromOverlayRow(raw as Record<string, unknown>);
          if (evt) events.push(evt);
        }
      }
    }
  }

  if (events.length === 0) return null;

  return {
    id: backtest.id,
    label,
    color,
    points: buildCumulativeFromEvents(events),
  };
}

/**
 * Merge series onto a shared **daily** axis for comparison.
 * Each series contributes at most one value per calendar day (end-of-day cumulative).
 * Days with no exits for a series hold the previous day's cumulative.
 */
export function mergeTradingPnlSeriesForChart(
  series: TradingPnlSeries[]
): Array<Record<string, number | string>> {
  const daily = series.map((s) => ({
    ...s,
    points: toDailyEndOfDayPoints(s.points),
  }));

  const allDays = new Set<number>();
  for (const s of daily) {
    for (const p of s.points) allDays.add(p.timeMs);
  }
  const sortedDays = Array.from(allDays).sort((a, b) => a - b);
  if (sortedDays.length === 0) return [];

  const stepAt = (points: TradingPnlSeriesPoint[], dayMs: number): number => {
    let val = 0;
    for (const p of points) {
      if (p.timeMs <= dayMs) val = p.cumulativePnl;
      else break;
    }
    return val;
  };

  return sortedDays.map((timeMs) => {
    const row: Record<string, number | string> = {
      timeMs,
      label: formatTradingPnlDayLabel(timeMs),
    };
    for (const s of daily) {
      row[s.id] = stepAt(s.points, timeMs);
    }
    return row;
  });
}

export function tradingPnlSeriesHasData(series: TradingPnlSeries[]): boolean {
  return series.some((s) => s.points.length > 0);
}
